import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { CreditCard, Wallet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [amount, setAmount] = useState('');
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchRecentTransactions();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const fetchRecentTransactions = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/transactions`);
      setRecentTransactions(data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handleSubmit = async (paymentType) => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    setLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      await axios.post(
        `${BACKEND_URL}/api/transactions`,
        {
          amount: parseFloat(amount),
          payment_type: paymentType,
          date: today
        }
      );

      setAmount('');
      fetchRecentTransactions();
    } catch (error) {
      console.error('Error creating transaction:', error);
    } finally {
      setLoading(false);
    }
  };

  const today = format(new Date(), 'EEEE, MMMM d, yyyy');

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <div className="max-w-2xl mx-auto px-6 pt-8">
        {/* Date Display */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Today
          </p>
          <h2 className="text-lg text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dashboard-current-date">
            {today}
          </h2>
        </div>

        {/* Amount Input Section */}
        <div className="bg-white rounded-2xl p-8 mb-8 border border-slate-200">
          <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Enter Amount
          </label>
          
          <div className="relative mb-8">
            <span className="absolute left-0 top-1/2 -translate-y-1/2 text-5xl sm:text-6xl font-bold text-slate-400 tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>
              ₹
            </span>
            <input
              ref={inputRef}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className="w-full text-5xl sm:text-6xl font-bold text-slate-900 text-center tracking-tighter border-none outline-none bg-transparent"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="dashboard-amount-input"
            />
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4">
            <Button
              onClick={() => handleSubmit('khata')}
              disabled={loading || !amount}
              className="h-16 bg-blue-50 text-blue-900 border border-blue-200 hover:bg-blue-100 rounded-2xl font-semibold text-lg transition-all hover:-translate-y-[2px] hover:shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="dashboard-khata-button"
            >
              <CreditCard className="mr-2 h-5 w-5" />
              Khata
            </Button>

            <Button
              onClick={() => handleSubmit('cash')}
              disabled={loading || !amount}
              className="h-16 bg-blue-600 text-white hover:bg-blue-700 rounded-2xl font-semibold text-lg transition-all hover:-translate-y-[2px] hover:shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="dashboard-cash-button"
            >
              <Wallet className="mr-2 h-5 w-5" />
              Cash
            </Button>
          </div>
        </div>

        {/* Recent Transactions */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Recent Transactions
          </h3>

          {recentTransactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 bg-white rounded-2xl border border-slate-200">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/4fe567e4-fea0-4ee6-a821-9b513bd89b32/images/e014d0f488d5c3fffb336b783415d9d57b1293eb084de7c04fd0051550100a1c.png" 
                alt="No transactions" 
                className="w-48 h-48 opacity-50 mb-4"
              />
              <p className="text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>No transactions yet</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="dashboard-recent-transactions-list">
              {recentTransactions.map((transaction, index) => (
                <div 
                  key={transaction.id} 
                  className={`flex justify-between items-center p-4 ${index !== recentTransactions.length - 1 ? 'border-b border-slate-100' : ''}`}
                  data-testid={`transaction-item-${index}`}
                >
                  <div>
                    <p className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {transaction.payment_type === 'khata' ? 'Khata' : 'Cash'}
                    </p>
                    <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {format(new Date(transaction.date), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    ₹{transaction.amount.toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;